import React, { useEffect, useState } from "react";
import { Sparkles, X, Heart } from "lucide-react";
import api from "../lib/api";

// Shared fetch — backend caches sign-pair results, so repeated calls are cheap.
export function useCompatibility(userId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/astrology/compatibility/${userId}`)
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId]);
  return { data, loading };
}

function pctColor(p) {
  if (p >= 75) return "#6FBF8B";
  if (p >= 50) return "#D4AF37";
  return "#E58CA0";
}

function SignPair({ your, their }) {
  return (
    <div className="flex items-center justify-center gap-3 text-[#FBF8F3]" data-testid="compat-sign-pair">
      <span className="flex items-center gap-1.5"><span className="text-2xl leading-none">{your?.symbol}</span><span className="text-sm">{your?.name}</span></span>
      <Heart size={14} className="text-[#E58CA0]" fill="currentColor" />
      <span className="flex items-center gap-1.5"><span className="text-2xl leading-none">{their?.symbol}</span><span className="text-sm">{their?.name}</span></span>
    </div>
  );
}

// Compact card for the profile — returns null when feature disabled or DOB missing.
export function CompatibilityCard({ data }) {
  const [open, setOpen] = useState(false);
  if (!data || !data.enabled) return null;
  if (!data.available && data.reason === "missing_dob") return null; // hide completely

  if (!data.available) {
    return (
      <div data-testid="compat-card-unavailable" className="mt-5 rounded-2xl border border-white/8 bg-[#141113]/60 p-4 text-center">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#C9A96E] flex items-center justify-center gap-1.5"><Sparkles size={13} /> Astrology Compatibility</div>
        <p className="text-sm text-[#9E978E] mt-2">Astrology compatibility is temporarily unavailable.</p>
      </div>
    );
  }

  return (
    <>
      <div data-testid="compat-card" className="mt-5 rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-b from-[#1C1016]/80 to-[#141113]/60 p-5">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#C9A96E] flex items-center justify-center gap-1.5 mb-3"><Sparkles size={13} /> Astrology Compatibility</div>
        <SignPair your={data.your_sign} their={data.their_sign} />
        <div className="text-center mt-3">
          <div className="font-serif text-5xl leading-none" style={{ color: pctColor(data.percentage) }} data-testid="compat-percentage">{data.percentage}%</div>
        </div>
        <p className="text-sm text-[#D8D2C9] text-center mt-3 line-clamp-2">{data.report}</p>
        <button data-testid="compat-view-btn" onClick={() => setOpen(true)}
          className="w-full mt-4 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#541224]/40 py-2 text-sm transition-colors">
          View Compatibility
        </button>
      </div>
      {open && <CompatibilityModal data={data} onClose={() => setOpen(false)} />}
    </>
  );
}

// Inline summary for the "Before you make your Date Offer" area.
export function CompatibilityInline({ data }) {
  if (!data || !data.enabled || !data.available) return null;
  return (
    <div data-testid="compat-inline" className="rounded-xl border border-[#D4AF37]/20 bg-[#0C0A0A]/50 p-3">
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#C9A96E] mb-2">Before you make your Date Offer</div>
      <div className="flex items-center justify-between">
        <SignPair your={data.your_sign} their={data.their_sign} />
        <span className="font-serif text-2xl" style={{ color: pctColor(data.percentage) }}>{data.percentage}%</span>
      </div>
      <p className="text-[11px] text-[#9E978E] mt-2 italic">Astrology is just for fun — your Date Offer is the real move.</p>
    </div>
  );
}

export function CompatibilityModal({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-sm p-0 sm:p-4" onClick={onClose} data-testid="compat-modal">
      <div className="w-full sm:max-w-md db-glass rounded-t-3xl sm:rounded-3xl border border-[#D4AF37]/25 max-h-[92vh] overflow-y-auto db-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] flex items-center gap-1.5"><Sparkles size={13} /> Astrology Compatibility</div>
            <button onClick={onClose} className="text-[#9E978E] hover:text-[#FBF8F3]"><X size={22} /></button>
          </div>
          <div className="db-gold-rule my-4" />
          <SignPair your={data.your_sign} their={data.their_sign} />
          <div className="text-center mt-4">
            <div className="font-serif text-6xl leading-none" style={{ color: pctColor(data.percentage) }}>{data.percentage}%</div>
            <div className="text-xs text-[#9E978E] mt-1">Compatibility</div>
          </div>

          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#C9A96E] mb-1.5">Compatibility overview</div>
            <p className="text-sm text-[#D8D2C9] leading-relaxed" data-testid="compat-report">{data.report}</p>
          </div>

          {data.strengths && (
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-[0.16em] text-[#6FBF8B] mb-1.5">Strengths</div>
              <p className="text-sm text-[#D8D2C9] leading-relaxed" data-testid="compat-strengths">{data.strengths}</p>
            </div>
          )}
          {data.challenges && (
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-[0.16em] text-[#E58CA0] mb-1.5">Challenges</div>
              <p className="text-sm text-[#D8D2C9] leading-relaxed" data-testid="compat-challenges">{data.challenges}</p>
            </div>
          )}

          <p className="text-[11px] text-[#7C756C] mt-5 pt-4 border-t border-white/8 italic" data-testid="compat-disclaimer">{data.disclaimer}</p>
        </div>
      </div>
    </div>
  );
}
